import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, TouchableOpacity } from 'react-native';

import Clipboard from '@react-native-clipboard/clipboard';

import ArrowAngled from '@/assets/icons/arrowAngled';
import Arrow from '@/assets/icons/chevronRight';
import CopyIcon from '@/assets/icons/copy';
import QuestionMarkCircle from '@/assets/icons/questionMarkCircle';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { formatDate } from '@/feature/bets-sbk/components/SbkBetDetail';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { GameResult } from '../GameResultItem';

type GameResultDetailsProps = {
    gameResult: GameResult;
    onArrowPress: () => void;
};

export const GameResultDetails = ({ gameResult, onArrowPress }: GameResultDetailsProps) => {
    const { t } = useTranslation(['arcade_game_results', 'common']);
    const { showToast } = useAlerts();

    const copyToClipboard = () => {
        Clipboard.setString(gameResult.id);
        showToast({ message: t('copied_entry_id'), toastType: 'success' });
    };

    const { launchIntercomMessenger } = useLaunchIntercomMessenger();

    return (
        <Box px="s16">
            <Row justifyContent="center" alignItems="center" py="s12" height={48}>
                <Box position="absolute" left={0} style={{ transform: [{ rotate: '180deg' }] }}>
                    <Pressable onPress={onArrowPress}>
                        <Arrow color={ds.colorValue.icon.primary} />
                    </Pressable>
                </Box>
                <Text variant="titleLarge2">{t('entry_details')}</Text>
            </Row>
            <Column style={styles.entryDetails} pt="s12">
                <Row
                    justifyContent="space-between"
                    p="s16"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    borderTopLeftRadius="r16"
                    borderTopRightRadius="r16"
                >
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('entry_id')}
                    </Text>
                    <Row gap="s12" alignItems="center">
                        <Text variant="bodyMedium2">{gameResult.id}</Text>
                        <TouchableOpacity onPress={copyToClipboard} hitSlop={10}>
                            <CopyIcon />
                        </TouchableOpacity>
                    </Row>
                </Row>
                <Row justifyContent="space-between" p="s16" backgroundColor={ds.colorToken.background.neutral.subtler}>
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('common:submitted')}
                    </Text>
                    <Text variant="bodyMedium2">{formatDate(gameResult.created_at)}</Text>
                </Row>
                <Row justifyContent="space-between" p="s16" backgroundColor={ds.colorToken.background.neutral.subtler}>
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('common:closed')}
                    </Text>
                    <Text variant="bodyMedium2">{formatDate(gameResult.settled_at)}</Text>
                </Row>
                <Row justifyContent="space-between" p="s16" backgroundColor={ds.colorToken.background.neutral.subtler}>
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('result')}
                    </Text>
                    <Text variant="bodyMedium2" textTransform="capitalize">
                        {gameResult.status.toLowerCase()}
                    </Text>
                </Row>
                <Row
                    justifyContent="space-between"
                    p="s16"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    borderBottomLeftRadius="r16"
                    borderBottomRightRadius="r16"
                >
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('entry_amount')}
                    </Text>
                    <Text variant="bodyMedium2">{toLocaleCurrency(gameResult.stake)}</Text>
                </Row>
            </Column>
            <Row mt="s16" mb="s24">
                <TouchableOpacity onPress={launchIntercomMessenger}>
                    <Row
                        justifyContent="space-between"
                        alignItems="center"
                        borderRadius="r16"
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        p="s16"
                        width="100%"
                    >
                        <Row gap="s12" alignItems="center">
                            <QuestionMarkCircle />
                            <Text variant="bodyMedium2" color={ds.colorToken.text.brand}>
                                {t('help_and_support')}
                            </Text>
                        </Row>
                        <ArrowAngled />
                    </Row>
                </TouchableOpacity>
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    entryDetails: {
        gap: 1,
    },
});
